import React, { useState, useEffect, useRef } from "react";
import { Col, Container, Row, Table, Modal, Form, Dropdown } from "react-bootstrap";
import API_DOMAIN from "../config/config";

import { TextInputForm } from "../components/Forms";
import { ClickButton } from "../components/Buttons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import { PageHead } from "../components/PageTitle";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { PDFDownloadLink } from "@react-pdf/renderer";
import OverallSalaryPDF from "./overall_salary_report_pdf";

const WeeklySalaryCreation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { type = "create", rowData } = location.state || {};


  console.log("Row Data:", rowData);

  const [staffList, setStaffList] = useState([]);
  const [weeklyData, setWeeklyData] = useState({}); // Aggregated weekly data per staff
  console.log(weeklyData, "weekly");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [fetching, setFetching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savedDataLoaded, setSavedDataLoaded] = useState(false);
  const isSubmittingRef = useRef(false);

  // Advance Modal
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [advanceStaffId, setAdvanceStaffId] = useState("");
  const [advanceStaff, setAdvanceStaff] = useState("");
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [advanceType, setAdvanceType] = useState("add");
  const [advanceLoading, setAdvanceLoading] = useState(false);

  // Fetch attendance data between start and end date
  const fetchWeeklyAttendance = async () => {
    if (!startDate || !endDate) {
      toast.warning("Please select both start and end dates");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.error("Start date cannot be after end date");
      return;
    }

    setFetching(true);

    try {
      const res = await fetch(`${API_DOMAIN}/attendance.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "listAttendanceByRange",
          start_date: startDate,
          end_date: endDate,
        }),
      });

      const result = await res.json();

      if (result.head?.code === 200) {
        const attendances = result.body.attendance || [];

        // Initialize aggregation
        const aggregated = {};
        staffList.forEach((staff) => {
          const name = staff.staff_name;
          aggregated[name] = {
            staff_name: name,
            staffId: staff.staff_id,
            advanceBalance: Number(staff.advance_balance || 0),
            totalGross: 0,
            totalDeduction: 0,
            totalNet: 0,
            daysWorked: 0,
            halfDays: 0,
            advanceDeductionThisWeek: "",
            finalPayable: 0,
            maxPossibleDeduction: 0,
          };
        });

        // Aggregate from all days
        attendances.forEach((attendance) => {
          (attendance.data || []).forEach((item) => {
            const name = item.staff_name;
            if (!aggregated[name]) return;

            const wages = Number(item.wages || 0);
            const deduction = Number(item.deduction || 0);
            const gross = wages + deduction;

            aggregated[name].totalGross += gross;
            aggregated[name].totalDeduction += deduction;
            aggregated[name].totalNet += wages;

            const fh = item.firstHalf || "absent";
            const sh = item.secondHalf || "absent";
            if (fh === "present" && sh === "present")
              aggregated[name].daysWorked++;
            else if (fh === "present" || sh === "present")
              aggregated[name].halfDays++;
          });
        });

        // Finalize: calculate max possible advance recovery and initial final payable
        Object.keys(aggregated).forEach((name) => {
          const rec = aggregated[name];
          const maxDeduct = Math.min(rec.advanceBalance, rec.totalNet);
          rec.maxPossibleDeduction = maxDeduct;
          rec.advanceDeductionThisWeek = ""; // User will set
          rec.finalPayable = rec.totalNet;
        });

        setWeeklyData(aggregated);
        toast.success(`Weekly data loaded: ${attendances.length} day(s)`);
      } else {
        toast.error(result.head?.msg || "Failed to fetch attendance");
        setWeeklyData({});
      }
    } catch (err) {
      console.error(err);
      toast.error("Network error while fetching weekly data");
      setWeeklyData({});
    } finally {
      setFetching(false);
    }
  };

  // Update advance deduction for a staff (this week recovery)
  const setAdvanceDeductionThisWeek = (staffName, value) => {
    let amount = value === "" ? "" : Number(value) || 0;

    setWeeklyData((prev) => {
      const rec = prev[staffName];
      if (!rec) return prev;

      const maxAllowed = rec.maxPossibleDeduction;

      // Allow empty input temporarily
      if (value === "") {
        return {
          ...prev,
          [staffName]: {
            ...rec,
            advanceDeductionThisWeek: "",
            finalPayable: rec.totalNet,
          },
        };
      }

      // If user types more than allowed → cap it and show warning
      if (amount > maxAllowed) {
        toast.warning(
          `Max recovery for ${staffName} is ₹${maxAllowed.toFixed(
            2
          )}. Amount adjusted automatically.`
        );
        amount = maxAllowed; // Auto-correct to maximum
      }

      return {
        ...prev,
        [staffName]: {
          ...rec,
          advanceDeductionThisWeek: amount,
          finalPayable: rec.totalNet - amount,
        },
      };
    });
  };

  // Auto recover maximum possible advance for all staff
  const autoRecoverAll = () => {
    setWeeklyData((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((name) => {
        const rec = updated[name];
        const maxDeduct = rec.maxPossibleDeduction;
        rec.advanceDeductionThisWeek = maxDeduct;
        rec.finalPayable = rec.totalNet - maxDeduct;
      });
      return updated;
    });
    toast.success("Maximum possible advance recovered for all staff");
  };

  // Prepare payload array for backend
  const preparePayloadData = () => {
    return Object.entries(weeklyData).map(([staffName, rec]) => ({
      staff_name: rec.staff_name,
      staffId: rec.staffId,
      advanceBalance: rec.advanceBalance,
      totalGross: rec.totalGross,
      totalDeduction: rec.totalDeduction,
      totalNet: rec.totalNet,
      daysWorked: rec.daysWorked,
      halfDays: rec.halfDays,
      advanceDeductionThisWeek: rec.advanceDeductionThisWeek,
      finalPayable: rec.finalPayable,
      maxPossibleDeduction: rec.maxPossibleDeduction,
    }));
  };

  // Handle Submit (Create/Update)
  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;

    setLoading(true);
    isSubmittingRef.current = true;

    console.log("===== Weekly Salary Submission Debug Start =====");
    console.log("Submission Mode:", type);
    console.log("Row Data Raw:", rowData);
    console.log("Row Data Salary Field:", rowData?.data);

    if (!startDate || !endDate) {
      toast.error("Please select both dates");
      isSubmittingRef.current = false;
      setLoading(false);
      return;
    }

    const payloadData = preparePayloadData();
    console.log("Prepared Payload Data:", payloadData);

    // -------- SAFE old data extraction ----------
    let oldData = [];

    if (type === "edit") {
      try {
        const incoming = rowData?.data;

        if (!incoming) {
          console.warn("No salary_data found in rowData. oldData will remain empty");
        } else if (typeof incoming === "string") {
          console.log("Old salary received as string JSON");
          oldData = JSON.parse(incoming);
        } else if (Array.isArray(incoming)) {
          console.log("Old salary received as valid object array");
          oldData = incoming;
        } else {
          console.warn("Unknown salary_data format. Forcing empty fallback", incoming);
          oldData = [];
        }

        console.log("Final normalized Old Salary Data:", oldData);
      } catch (e) {
        console.error("Failed to normalize old salary_data", rowData?.data);
        console.error("Parser Error:", e);
        oldData = [];
      }
    } else {
      console.log("Create Mode Detected → Skipping old salary lookup");
    }


    // --------- Validate Deductions ----------
    for (const item of payloadData) {
      const rec = weeklyData[item.staff_name];
      console.log("Weekly Data Record For", item.staff_name, ":", rec);

      const newDed = Number(item.advanceDeductionThisWeek) || 0;
      if (!rec) {
        console.warn("No weekly reference record found for", item.staff_name);
        continue;
      }

      if (newDed > rec.totalNet) {
        toast.error(
          `Recovery for ${item.staff_name} cannot exceed net wage of ₹${rec.totalNet.toFixed(2)}`
        );
        isSubmittingRef.current = false;
        setLoading(false);
        return;
      }
    }

    const isEdit = type === "edit";
    let wsyId;
    let initialResult;

    try {
      // ---------------- Initial Save ----------------
      const initialPayload = {
        action: isEdit ? "updateWeeklySalary" : "createWeeklySalary",
        ...(isEdit && { weekly_salary_id: rowData.weekly_salary_id }),
        from_date: startDate,
        to_date: endDate,
        data: payloadData,
      };

      console.log("Initial API Payload:", initialPayload);

      const res = await fetch(`${API_DOMAIN}/weekly_salary.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(initialPayload),
      });

      const result = await res.json();
      initialResult = result;

      console.log("Initial Save API Response:", result);

      if (result.head?.code !== 200) {
        toast.error(result.head?.msg || "Failed to save weekly salary");
        isSubmittingRef.current = false;
        setLoading(false);
        return;
      }

      wsyId = isEdit ? rowData.weekly_salary_id : result.body?.weekly_salary_id;
      console.log("Resolved Weekly Salary ID:", wsyId);

      if (!wsyId) {
        toast.error("Failed to retrieve salary ID");
        isSubmittingRef.current = false;
        setLoading(false);
        return;
      }

      // ---------------- Adjust Recoveries ----------------
      let adjustmentErrors = false;
      const advanceIds = new Map();

      for (const item of payloadData) {
        const oldRec = oldData?.find((s) => s.staff_name === item.staff_name);
        console.log(`Old Record for ${item.staff_name} :`, oldRec);

        const oldDed = oldRec
          ? Number(
            oldRec.advanceDeductionThisWeek ??
            oldRec.advance_deduction_this_week ??
            oldRec.deduction ??
            0
          )
          : 0;

        const newDed = Number(item.advanceDeductionThisWeek) || 0;

        console.log("Salary Data Sample (first old record):", oldData?.[0]);
        console.log("Payload Sample (first payload record):", payloadData?.[0]);
        console.log(
          `Comparing Deductions → ${item.staff_name} | Old: ${oldDed} | New: ${newDed}`
        );

        if (newDed !== oldDed) {
          try {
            const adjRes = await fetch(`${API_DOMAIN}/weekly_salary.php`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                action: "setWeeklySalaryRecovery",
                weekly_salary_id: wsyId,
                staff_id: item.staffId,
                amount: newDed,
                date: endDate,
              }),
            });

            const adjResult = await adjRes.json();
            console.log(
              `Adjustment API Response for ${item.staff_name}:`,
              adjResult
            );

            if (adjResult.head?.code === 200) {
              const advId = adjResult.body?.advance_id;
              if (advId) advanceIds.set(item.staffId, advId);
            } else {
              adjustmentErrors = true;
              console.error(
                `Adjustment failed for ${item.staff_name}:`,
                adjResult
              );
            }
          } catch (err) {
            adjustmentErrors = true;
            console.error(
              `Network error for adjustment ${item.staff_name}:`,
              err
            );
          }
        }
      }

      // ---------------- Final Update with Advance IDs ----------------
      const updatedData = payloadData.map((item) => ({
        ...item,
        ...(advanceIds.has(item.staffId) && {
          advance_id: advanceIds.get(item.staffId),
        }),
      }));

      console.log("Final Data Sent for Update:", updatedData);

      const updatePayload = {
        action: "updateWeeklySalary",
        weekly_salary_id: wsyId,
        data: updatedData,
      };

      const updateRes = await fetch(`${API_DOMAIN}/weekly_salary.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatePayload),
      });

      const updateResult = await updateRes.json();
      console.log("Final Update Response:", updateResult);

      if (updateResult.head?.code !== 200) {
        console.error("Failed to update with advance_ids:", updateResult);
      }

      toast.success(
        isEdit
          ? "Weekly Salary Updated Successfully!"
          : "Weekly Salary Created!"
      );

      if (adjustmentErrors) {
        toast.warning(
          "Weekly salary saved, but some adjustments may have failed. Check logs."
        );
      }

      console.log("===== Weekly Salary Submission Completed =====");

      setTimeout(() => {
        isSubmittingRef.current = false;
        setLoading(false);
        navigate(-1);
      }, 1200);
    } catch (err) {
      console.error("Global Catch Error:", err);
      toast.error("Network error");
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };


  // Load staff list and set default week
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const res = await fetch(`${API_DOMAIN}/staff.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "listStaff" }),
        });
        const data = await res.json();

        if (data.head?.code === 200) {
          setStaffList(data.body.staff || []);

          if (type === "edit" && rowData) {
            setStartDate(rowData.from_date || "");
            setEndDate(rowData.to_date || "");
          } else {
            // Default: Last 7 days (including today)
            const today = new Date();
            const lastWeek = new Date(today);
            lastWeek.setDate(today.getDate() - 6); // 7 days total

            setStartDate(lastWeek.toISOString().split("T")[0]);
            setEndDate(today.toISOString().split("T")[0]);
          }
        } else {
          toast.error("Failed to load staff list");
        }
      } catch (err) {
        toast.error("Network error loading staff");
      }
    };

    fetchStaff();
  }, [type, rowData]);

  // Auto fetch when dates or staff list changes
  useEffect(() => {
    if (staffList.length > 0 && startDate && endDate) {
      fetchWeeklyAttendance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, staffList]);

  // Load saved deductions for edit mode
  useEffect(() => {
    if (
      type === "edit" &&
      rowData?.weekly_salary_id &&
      Object.keys(weeklyData).length > 0 &&
      !savedDataLoaded
    ) {
      const fetchSavedData = async () => {
        try {
          const res = await fetch(`${API_DOMAIN}/weekly_salary.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action: "getWeeklySalary",
              weekly_salary_id: rowData.weekly_salary_id,
            }),
          });

          const result = await res.json();

          if (result.head?.code === 200) {
            const savedData = result.body.salary_data || [];
            setWeeklyData((prev) => {
              const updated = { ...prev };
              savedData.forEach((item) => {
                const name = item.staff_name;
                if (updated[name]) {
                  updated[name].advanceBalance = Number(item.advanceBalance);
                  const savedDeduction =
                    Number(item.advanceDeductionThisWeek) || 0;
                  updated[name].advanceDeductionThisWeek = savedDeduction;
                  updated[name].finalPayable =
                    updated[name].totalNet - savedDeduction;
                  updated[name].maxPossibleDeduction = Math.min(
                    updated[name].advanceBalance,
                    updated[name].totalNet
                  );
                  if (item.advance_id) {
                    updated[name].advance_id = item.advance_id;
                  }
                }
              });
              return updated;
            });
            setSavedDataLoaded(true);
            toast.info("Loaded saved weekly salary data");
          } else {
            toast.error("Failed to load saved weekly salary data");
          }
        } catch (err) {
          console.error(err);
          toast.error("Network error loading saved data");
        }
      };

      fetchSavedData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, rowData, weeklyData, savedDataLoaded]);

  // Handle Advance Add/Recover
  const handleAdvanceSubmit = async () => {
    if (!advanceStaff) {
      toast.error("Please select employee");
      return;
    }
    if (!advanceAmount || Number(advanceAmount) <= 0) {
      toast.error("Please enter valid amount");
      return;
    }

    setAdvanceLoading(true);

    try {
      const res = await fetch(`${API_DOMAIN}/attendance.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "addAdvance",
          staff_id: advanceStaffId,
          staff_name: advanceStaff,
          amount: Number(advanceAmount),
          type: advanceType,
          recovery_mode: "direct",
          date: endDate || new Date().toISOString().split("T")[0],
        }),
      });

      const result = await res.json();

      if (result.head?.code === 200) {
        toast.success(
          advanceType === "add"
            ? "Advance added successfully"
            : "Advance recovered successfully"
        );

        // Refresh staff list to update advance_balance
        const resStaff = await fetch(`${API_DOMAIN}/staff.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "listStaff" }),
        });
        const dataStaff = await resStaff.json();
        if (dataStaff.head?.code === 200) {
          setStaffList(dataStaff.body.staff || []);
        }

        setShowAdvanceModal(false);
        setAdvanceStaffId("");
        setAdvanceStaff("");
        setAdvanceAmount("");
        setAdvanceType("add");
      } else {
        toast.error(result.head?.msg || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setAdvanceLoading(false);
    }
  };

  const handleAdvanceClose = () => {
    setShowAdvanceModal(false);
    setAdvanceStaff("");
    setAdvanceAmount("");
    setAdvanceType("add");
  };
  const periodString = [startDate, endDate].filter(Boolean).join(" - ");

  return (
    <div className="main my-3">
      <ToastContainer position="top-center" />

      <Container>
        <Row>
          <Col>
            <PageHead
              pagehead={
                type === "edit"
                  ? "Edit Weekly Salary"
                  : "Weekly Salary Creation"
              }
              onClick={() => navigate(-1)}
            />
          </Col>
        </Row>

        <Row className="mb-4 align-items-end">
          <Col md={3}>
            <Form.Label>From Date</Form.Label>
            <DatePicker
              selected={startDate ? new Date(startDate) : null}
              onChange={(date) =>
                setStartDate(date ? date.toISOString().split("T")[0] : "")
              }
              dateFormat="dd/MM/yyyy"
              className="form-control"
              placeholderText="Start date"
              disabled={type === "edit"}
            />
          </Col>
          <Col md={3}>
            <Form.Label>To Date</Form.Label>
            <DatePicker
              selected={endDate ? new Date(endDate) : null}
              onChange={(date) =>
                setEndDate(date ? date.toISOString().split("T")[0] : "")
              }
              dateFormat="dd/MM/yyyy"
              className="form-control"
              placeholderText="End date"
              disabled={type === "edit"}
            />
          </Col>
          <Col md={2}>
            <ClickButton
              label="Refresh"
              onClick={fetchWeeklyAttendance}
              disabled={fetching}
            />
          </Col>
          <Col md={2}>
            <ClickButton
              label="Auto Recover All"
              variant="warning"
              onClick={autoRecoverAll}
              disabled={fetching || staffList.length === 0}
            />
          </Col>
          <Col md={2} className="text-end">
            <ClickButton
              label="Add Advance"
              variant="danger"
              onClick={() => setShowAdvanceModal(true)}
            />
          </Col>
          <Col md={2} className="text-end">
            <PDFDownloadLink
              document={
                <OverallSalaryPDF
                  data={weeklyData}
                  fromDate={startDate}
                  toDate={endDate}
                />
              }
              fileName={`Overall_salary_report_${periodString.replace(/\//g, "_")}.pdf`}
            >
              {({ url, loading }) => (
                <button
                  disabled={loading}
                  className="create-btn"
                  style={{ width: "100%" }}
                  download
                >
                  {loading ? "Preparing Overall PDF..." : "Overall Download PDF"}
                </button>
              )}
            </PDFDownloadLink>
          </Col>

        </Row>


        {fetching ? (
          <div className="text-center my-5">
            <Spinner animation="border" variant="primary" />
            <p>Loading weekly salary data...</p>
          </div>
        ) : (
          <Table bordered hover responsive>
            <thead className="table-light">
              <tr>
                <th>Staff Name</th>
                <th>Advance Balance</th>
                <th>Full Days</th>
                <th>Half Days</th>
                <th>Gross Wage</th>
                {/* <th>Other Deduction</th> */}
                <th>Payable Wage</th>
                <th style={{ backgroundColor: "#fff3cd" }}>
                  <strong>
                    Recover Advance
                    <br />
                    <small>(This Week)</small>
                  </strong>
                </th>
                <th style={{ backgroundColor: "#d4edda" }}>
                  <strong>Final Pay Amount</strong>
                </th>
              </tr>
            </thead>
            <tbody>
              {staffList.map((staff) => {
                const rec = weeklyData[staff.staff_name] || {
                  staff_name: staff.staff_name,
                  advanceBalance: Number(staff.advance_balance || 0),
                  totalGross: 0,
                  totalDeduction: 0,
                  totalNet: 0,
                  daysWorked: 0,
                  halfDays: 0,
                  advanceDeductionThisWeek: "0",
                  finalPayable: 0,
                  maxPossibleDeduction: 0,
                };

                return (
                  <tr key={staff.staff_id}>
                    <td className="fw-bold">{staff.staff_name}</td>
                    <td>₹{rec.advanceBalance.toFixed(2)}</td>
                    <td>{rec.daysWorked}</td>
                    <td>{rec.halfDays}</td>
                    <td>₹{rec.totalGross.toFixed(2)}</td>
                    {/* <td>₹{rec.totalDeduction.toFixed(2)}</td> */}
                    <td className="fw-bold">₹{rec.totalNet.toFixed(2)}</td>
                    <td>
                      <TextInputForm
                        type="number"
                        value={rec.advanceDeductionThisWeek}
                        onChange={(e) =>
                          setAdvanceDeductionThisWeek(
                            staff.staff_name,
                            e.target.value
                          )
                        }
                        placeholder="0"
                        style={{ width: "120px" }}
                        min="0"
                        max={rec.maxPossibleDeduction}
                        step="1"
                        disabled={rec.maxPossibleDeduction === 0}
                      />
                      <small className="text-muted d-block">
                        Max: ₹{rec.maxPossibleDeduction.toFixed(2)}
                      </small>
                    </td>
                    <td className="fw-bold text-success bg-light">
                      ₹{rec.finalPayable.toFixed(2)}
                    </td>
                  </tr>
                );
              })}

              {/* Grand Totals */}
              {staffList.length > 0 && (
                <>
                  <tr className="table-info fw-bold">
                    <td colSpan={5} className="text-end">
                      Total Payable This Week:
                    </td>
                    <td>
                      ₹
                      {Object.values(weeklyData)
                        .reduce((sum, s) => sum + s.totalNet, 0)
                        .toFixed(2)}
                    </td>
                    <td></td>
                    <td></td>
                  </tr>
                  <tr className="table-success fw-bold">
                    <td colSpan={7} className="text-end">
                      Final Amount to Pay (After Advance Recovery):
                    </td>
                    <td>
                      ₹
                      {Object.values(weeklyData)
                        .reduce((sum, s) => sum + s.finalPayable, 0)
                        .toFixed(2)}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </Table>
        )}

        {/* Advance Modal */}
        <Modal
          show={showAdvanceModal}
          onHide={handleAdvanceClose}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {advanceType === "add" ? "Add Advance" : "Recover Advance"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Employee</Form.Label>
                  <Form.Select
                    value={advanceStaffId}
                    onChange={(e) => {
                      const selected = staffList.find(
                        (s) => s.staff_id === e.target.value
                      );
                      setAdvanceStaffId(selected?.staff_id || "");
                      setAdvanceStaff(selected?.staff_name || "");
                    }}
                    disabled={advanceLoading}
                  >
                    <option value="">Select Employee</option>
                    {staffList.map((s) => (
                      <option key={s.staff_id} value={s.staff_id}>
                        {s.staff_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Type</Form.Label>
                  <div>
                    <Form.Check
                      inline
                      type="radio"
                      label="Add"
                      value="add"
                      checked={advanceType === "add"}
                      onChange={(e) => setAdvanceType(e.target.value)}
                      disabled={advanceLoading}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      label="Recover"
                      value="less"
                      checked={advanceType === "less"}
                      onChange={(e) => setAdvanceType(e.target.value)}
                      disabled={advanceLoading}
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Amount</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter amount"
                    value={advanceAmount}
                    onChange={(e) => setAdvanceAmount(e.target.value)}
                    min="0"
                    step="1"
                    disabled={advanceLoading}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <ClickButton
              label={advanceType === "add" ? "Add Advance" : "Recover"}
              onClick={handleAdvanceSubmit}
              disabled={advanceLoading}
            />
            <ClickButton
              label="Cancel"
              variant="secondary"
              onClick={handleAdvanceClose}
            />
          </Modal.Footer>
        </Modal>

        <div className="text-center my-5">
          {loading ? (
            <Spinner animation="border" variant="primary" />
          ) : (
            <div className="d-flex justify-content-center gap-3">
              <ClickButton
                label={
                  type === "edit"
                    ? "Update Weekly Salary"
                    : "Create Weekly Salary"
                }
                onClick={handleSubmit}
                disabled={
                  fetching ||
                  loading ||
                  isSubmittingRef.current ||
                  staffList.length === 0 ||
                  !startDate ||
                  !endDate
                }
              />
              <ClickButton
                label="Cancel"
                variant="secondary"
                onClick={() => navigate(-1)}
              />
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default WeeklySalaryCreation;
